import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, TrendingDown, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function MetaAdsCampaignsSection({ oauthConnected, configuredAccountId, currency = 'EUR' }) {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, paused

  // Format currency helper
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  };

  // Load accounts and campaigns when OAuth is connected
  useEffect(() => {
    if (oauthConnected) {
      loadData();
    }
  }, [oauthConnected]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load accounts
      const accountsRes = await authAxios.get('/marketing/meta-ads/accounts');
      setAccounts(accountsRes.data);

      // Load campaigns if account is configured
      if (configuredAccountId) {
        const campaignsRes = await authAxios.get('/marketing/meta-ads/campaigns');
        console.log('[META_CAMPAIGNS_LOADED]', campaignsRes.data);
        if (campaignsRes.data && campaignsRes.data.length > 0) {
          console.log('[META_CAMPAIGN_NULL_CHECK] First campaign:', campaignsRes.data[0]);
        }
        setCampaigns(campaignsRes.data);
      }
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCampaigns = () => {
    if (filter === 'active') {
      return campaigns.filter(c => c.status === 'ACTIVE');
    }
    if (filter === 'paused') {
      return campaigns.filter(c => c.status === 'PAUSED');
    }
    return campaigns;
  };

  const getTotalSpendToday = () => {
    return campaigns.reduce((sum, c) => sum + (c.spend_today || 0), 0);
  };

  const getActiveCampaignsCount = () => {
    return campaigns.filter(c => c.status === 'ACTIVE').length;
  };

  const getPausedCampaignsCount = () => {
    return campaigns.filter(c => c.status === 'PAUSED').length;
  };

  const filteredCampaigns = getFilteredCampaigns();

  if (!oauthConnected) {
    return null;
  }

  if (!configuredAccountId) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <p className="text-sm text-amber-800">
            Nenhuma conta de anúncios configurada. Conecte com Meta ou selecione uma conta existente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Campanhas Meta</CardTitle>
            <CardDescription className="text-xs">
              {campaigns.length} campanhas • {getActiveCampaignsCount()} ativas
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Metrics row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-md bg-slate-50">
            <p className="text-xs text-muted-foreground">Ativas</p>
            <p className="text-lg font-semibold text-green-600">{getActiveCampaignsCount()}</p>
          </div>
          <div className="p-3 rounded-md bg-slate-50">
            <p className="text-xs text-muted-foreground">Pausadas</p>
            <p className="text-lg font-semibold text-gray-500">{getPausedCampaignsCount()}</p>
          </div>
          <div className="p-3 rounded-md bg-slate-50">
            <p className="text-xs text-muted-foreground">Gasto hoje</p>
            <p className="text-lg font-semibold">{formatCurrency(getTotalSpendToday())}</p>
          </div>
        </div>

        {/* Filter buttons */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            Todas ({campaigns.length})
          </Button>
          <Button
            variant={filter === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('active')}
          >
            Ativas ({getActiveCampaignsCount()})
          </Button>
          <Button
            variant={filter === 'paused' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('paused')}
          >
            Pausadas ({getPausedCampaignsCount()})
          </Button>
        </div>

        {/* Campaigns list */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Carregando campanhas...
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nenhuma campanha encontrada
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredCampaigns.map(campaign => (
              <div key={campaign.id} className="p-3 rounded-md border bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{campaign.name}</p>
                      <Badge
                        variant={campaign.status === 'ACTIVE' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {campaign.status === 'ACTIVE' ? (
                          <Eye className="w-3 h-3 mr-1 inline" />
                        ) : (
                          <EyeOff className="w-3 h-3 mr-1 inline" />
                        )}
                        {campaign.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{campaign.objective}</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Orçamento</p>
                    <p className="font-semibold">{formatCurrency(campaign.daily_budget)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Gasto hoje</p>
                    <p className="font-semibold">{formatCurrency(campaign.spend_today)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Impressões</p>
                    <p className="font-semibold">{((campaign.impressions) ?? 0).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cliques</p>
                    <p className="font-semibold">{((campaign.clicks) ?? 0).toLocaleString('pt-BR')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-2 text-xs border-t pt-2">
                  <div>
                    <p className="text-muted-foreground">Resultados</p>
                    <p className="font-semibold">{((campaign.results) ?? 0).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CPC</p>
                    <p className="font-semibold">{formatCurrency(campaign.cpc)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CTR</p>
                    <p className="font-semibold">{((campaign.ctr) ?? 0).toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Custo/Resultado</p>
                    <p className="font-semibold">{formatCurrency(campaign.cost_per_result)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
